import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { dashboardApi } from '../api/auth';
import { isApiError } from '../lib/apiClient';
import './DashboardPage.css';

export function DashboardPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: dashboardApi.get,
  });

  if (isLoading) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div>
        <h1>Home</h1>
        <div className="card">
          <p className="error-text" style={{ marginTop: 0 }}>
            {isApiError(error) ? error.message : 'Could not load your dashboard.'}
          </p>
          <button className="btn btn--ghost" onClick={() => refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const { stats, clients, upcomingEvents, recentInvoices } = data;

  return (
    <div>
      <h1>Home</h1>

      <div className="dash__stats">
        <StatCard label="Families" value={stats.totalClients} to="/families" />
        <StatCard label="Upcoming shifts" value={stats.upcomingShifts} to="/schedule" />
        <StatCard label="Pending invoices" value={stats.pendingInvoices} to="/invoices" />
      </div>

      <section className="dash__section">
        <div className="dash__section-head">
          <h2>Your families</h2>
          <Link to="/families" className="dash__more">
            View all
          </Link>
        </div>
        {clients.length === 0 ? (
          <EmptyCard text="No connected families yet. Connect using a caregiver's access code in the mobile app." />
        ) : (
          <div className="card dash__list">
            {clients.slice(0, 5).map((c) => (
              <div key={c.connectionId} className="dash__row">
                <div>
                  <div className="dash__row-title">{c.youthSnapshot?.name ?? 'Youth'}</div>
                  <div className="muted dash__row-sub">{c.caregiverEmail || '—'}</div>
                </div>
                <div className="muted dash__row-sub">
                  {c.nextEvent ? `Next: ${c.nextEvent.date} ${c.nextEvent.time}` : 'No upcoming shift'}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="dash__section">
        <div className="dash__section-head">
          <h2>Upcoming shifts</h2>
          <Link to="/schedule" className="dash__more">
            View all
          </Link>
        </div>
        {upcomingEvents.length === 0 ? (
          <EmptyCard text="Nothing scheduled." />
        ) : (
          <div className="card dash__list">
            {upcomingEvents.map((e) => (
              <div key={e.id} className="dash__row">
                <div>
                  <div className="dash__row-title">{e.title}</div>
                  <div className="muted dash__row-sub">
                    {e.youthName} · {e.location || 'No location'}
                  </div>
                </div>
                <div className="muted dash__row-sub">
                  {e.date} {e.time}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="dash__section">
        <div className="dash__section-head">
          <h2>Recent invoices</h2>
          <Link to="/invoices" className="dash__more">
            View all
          </Link>
        </div>
        {recentInvoices.length === 0 ? (
          <EmptyCard text="No invoices yet." />
        ) : (
          <div className="card dash__list">
            {recentInvoices.map((i) => (
              <div key={i.id} className="dash__row">
                <div>
                  <div className="dash__row-title">#{i.invoiceNumber}</div>
                  <div className="muted dash__row-sub">{i.status}</div>
                </div>
                <div className="dash__row-sub">
                  {i.currency} {i.totalAmount.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, to }: { label: string; value: number; to: string }) {
  return (
    <Link to={to} className="card dash__stat">
      <div className="dash__stat-value">{value}</div>
      <div className="muted dash__stat-label">{label}</div>
    </Link>
  );
}

function EmptyCard({ text }: { text: string }) {
  return (
    <div className="card">
      <p className="muted" style={{ margin: 0 }}>
        {text}
      </p>
    </div>
  );
}
